/**
 * Resuelve contenido conversacional para el panel Genie.
 */

import { getKnowledgeEntryById } from './entries';
import { resolveKnowledgeEntryId } from './mapper';
import { TIEBREAKER_SNIPPETS } from './snippets/tiebreakers';
import type {
  GenieConversationContext,
  GenieKnowledgeBlock,
  GenieKnowledgeEntry,
  GenieKnowledgeMoment,
  GenieKnowledgeResolveOptions,
  TemporalCourse,
} from './types';

const LOW_CONFIDENCE_THRESHOLD = 0.4;

function buildBlocksFromEntry(
  entry: GenieKnowledgeEntry,
  moment: GenieKnowledgeMoment,
  confidence?: number
): GenieKnowledgeBlock[] {
  const blocks: GenieKnowledgeBlock[] = [
    { type: 'headline', content: entry.conversation.headline },
  ];

  const showFull = confidence === undefined || confidence >= LOW_CONFIDENCE_THRESHOLD;

  if (moment === 'orientation') {
    if (entry.orientationHints?.whenToSuspect) {
      blocks.push({
        type: 'pearl',
        content: entry.orientationHints.whenToSuspect,
      });
    }
    return blocks;
  }

  if (!showFull) {
    return blocks;
  }

  if (entry.conversation.edxHighlights.length > 0) {
    blocks.push({
      type: 'bullets',
      content: entry.conversation.edxHighlights.slice(0, 4),
    });
  }

  for (const pearl of entry.conversation.pearls.slice(0, 2)) {
    blocks.push({ type: 'pearl', content: pearl });
  }

  if (moment === 'live_diagnosis' && entry.conversation.differentials?.length) {
    const diffLines = entry.conversation.differentials
      .slice(0, 3)
      .map(d => `${d.name}: ${d.distinguishingHint}`);
    blocks.push({ type: 'differential', content: diffLines });
  }

  if (entry.conversation.nextSteps?.length) {
    blocks.push({
      type: 'next_steps',
      content: entry.conversation.nextSteps.slice(0, 2),
    });
  }

  return blocks;
}

function toContext(
  entry: GenieKnowledgeEntry,
  moment: GenieKnowledgeMoment,
  confidence?: number
): GenieConversationContext {
  return {
    entryId: entry.id,
    name: entry.name,
    moment,
    blocks: buildBlocksFromEntry(entry, moment, confidence),
    hasMore: Boolean(entry.detail?.summary || (entry.detail?.fullPearls?.length ?? 0) > 0),
  };
}

export function getKnowledgeForLiveDiagnosis(
  patternId: string,
  options?: GenieKnowledgeResolveOptions
): GenieConversationContext | null {
  const entryId = resolveKnowledgeEntryId(patternId, {
    temporalCourse: options?.temporalCourse,
  });
  const entry = getKnowledgeEntryById(entryId);
  if (!entry) return null;
  return toContext(entry, options?.moment ?? 'live_diagnosis', options?.confidence);
}

export function getKnowledgeForOrientation(
  catalogId: string,
  options?: { temporalCourse?: TemporalCourse }
): GenieConversationContext | null {
  const entryId = resolveKnowledgeEntryId(catalogId, options);
  const entry = getKnowledgeEntryById(entryId);
  if (!entry) return null;
  return toContext(entry, 'orientation');
}

export function getKnowledgeForTiebreaker(
  tiebreakerId: string
): GenieConversationContext | null {
  const blocks = TIEBREAKER_SNIPPETS[tiebreakerId];
  if (!blocks?.length) return null;
  return {
    entryId: tiebreakerId,
    name: tiebreakerId === 'ela_vs_mmn' ? 'ELA vs NMM' : 'MG vs LEMS',
    moment: 'tiebreaker',
    blocks,
    hasMore: false,
  };
}

export function getTemporalCourseFromGenieAnswer(
  cursoTemporalAnswer?: string
): TemporalCourse | undefined {
  if (!cursoTemporalAnswer) return undefined;
  const map: Record<string, TemporalCourse> = {
    agudo: 'acute',
    subagudo: 'subacute',
    cronico: 'chronic',
  };
  return map[cursoTemporalAnswer];
}

export { resolveKnowledgeEntryId } from './mapper';
export { getKnowledgeEntryById, ALL_KNOWLEDGE_ENTRIES } from './entries';
export type {
  GenieConversationContext,
  GenieKnowledgeBlock,
  GenieKnowledgeEntry,
  GenieKnowledgeMoment,
  GenieKnowledgeResolveOptions,
  TemporalCourse,
} from './types';
